import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Trophy, Users, Coins, UploadSimple, Heart, Camera, Prohibit, ChatCircle } from "@phosphor-icons/react";
import ImageCropper from "../components/ImageCropper";
import VerifyFaceBanner from "../components/VerifyFaceBanner";
import LiveArchive from "../components/LiveArchive";
import LiveAppealCard from "../components/LiveAppealCard";
import FollowListModal from "../components/FollowListModal";

export default function Profile() {
    const { userId } = useParams();
    const { user: me, refresh } = useAuth();
    const [user, setUser] = useState(null);
    const [videos, setVideos] = useState([]);
    const [following, setFollowing] = useState(false);
    const [blockedByMe, setBlockedByMe] = useState(false);
    const [blockedMe, setBlockedMe] = useState(false);
    const [blockedList, setBlockedList] = useState([]);
    const [editing, setEditing] = useState(false);
    const [bio, setBio] = useState("");
    const [gender, setGender] = useState("");
    const [profilePic, setProfilePic] = useState("");
    const [name, setName] = useState("");
    const [uploadingPic, setUploadingPic] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const [followListKind, setFollowListKind] = useState(null); // null | "followers" | "following"
    const fileInputRef = useRef(null);
    const isMe = me?.id === userId;

    const load = async () => {
        const [u, v] = await Promise.all([
            api.get(`/users/${userId}`),
            api.get(`/users/${userId}/videos`),
        ]);
        setUser(u.data);
        setVideos(v.data);
        setBio(u.data.bio || "");
        setGender(u.data.gender || "");
        setProfilePic(u.data.profile_pic || "");
        setName(u.data.name || "");
        if (me && !isMe) {
            api.get(`/users/${userId}/is-following`).then(({ data }) => setFollowing(data.following)).catch(() => {});
            api.get(`/users/${userId}/block-status`).then(({ data }) => {
                setBlockedByMe(data.blocked_by_me);
                setBlockedMe(data.blocked_me);
            }).catch(() => {});
        }
    };

    useEffect(() => { load(); }, [userId, me?.id]);

    if (!user) return <div className="p-10 text-wta-muted">Loading…</div>;

    const onFollow = async () => {
        try {
            const { data } = await api.post("/follow", { user_id: userId });
            setFollowing(data.following);
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const onBlock = async () => {
        try {
            if (blockedByMe) {
                await api.delete(`/block/${userId}`);
                setBlockedByMe(false);
                toast.success(`Unblocked @${user.name}`);
            } else {
                if (!window.confirm(`Block @${user.name}? You won't see each other's posts and you'll both be unfollowed.`)) return;
                await api.post("/block", { user_id: userId });
                setBlockedByMe(true);
                setFollowing(false);
                toast.success(`Blocked @${user.name}`);
            }
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const loadBlockedList = async () => {
        try {
            const { data } = await api.get("/users/me/blocked");
            setBlockedList(data);
        } catch { /* silent */ }
    };

    const onUnblockFromList = async (uid) => {
        try {
            await api.delete(`/block/${uid}`);
            setBlockedList((l) => l.filter((b) => b.user.id !== uid));
            toast.success("Unblocked");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const onSave = async () => {
        try {
            await api.patch("/users/me", { name, bio, gender: gender || null, profile_pic: profilePic });
            toast.success("Profile updated");
            setEditing(false);
            await refresh();
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const onPickFile = () => fileInputRef.current?.click();

    const onFileSelected = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image file");
        if (f.size > 10 * 1024 * 1024) return toast.error("Max 10MB");
        const reader = new FileReader();
        reader.onload = () => setCropSrc(reader.result);
        reader.readAsDataURL(f);
    };

    const onCropConfirm = async (blob) => {
        setCropSrc(null);
        if (!blob) return;
        const form = new FormData();
        form.append("file", blob, "profile.png");
        setUploadingPic(true);
        try {
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            setProfilePic(fullUrl);
            toast.success("Photo ready — hit Save");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setUploadingPic(false);
        }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-5xl mx-auto">
            {isMe && <div className="mb-4"><VerifyFaceBanner /></div>}
            <div className="flex flex-col md:flex-row gap-6 md:items-end">
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-sm bg-wta-surface2 border border-wta-border overflow-hidden flex items-center justify-center font-heading text-6xl shrink-0">
                    {user.profile_pic ? <img src={user.profile_pic} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">@{user.name}</div>
                    <h1 className="font-heading text-4xl md:text-6xl tracking-tight uppercase">{user.name}</h1>
                    {user.bio && <p className="text-white/70 mt-2 max-w-xl">{user.bio}</p>}

                    <div className="grid grid-cols-4 gap-2 mt-4 max-w-md">
                        <Stat label="Wins" value={user.wins} icon={Trophy} />
                        <StatButton
                            label="Followers"
                            value={user.followers_count}
                            icon={Users}
                            onClick={() => setFollowListKind("followers")}
                            testid="stat-followers-btn"
                        />
                        <StatButton
                            label="Following"
                            value={user.following_count}
                            onClick={() => setFollowListKind("following")}
                            testid="stat-following-btn"
                        />
                        <Stat label="Earnings" value={`$${(user.earnings || 0).toFixed(0)}`} icon={Coins} />
                    </div>

                    <div className="flex gap-3 mt-5">
                        {isMe ? (
                            <>
                                <button data-testid="edit-profile-btn" onClick={() => { setEditing(true); loadBlockedList(); }} className="btn-ghost px-5 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Edit Profile</button>
                                <Link to="/upload" data-testid="goto-upload" className="btn-primary px-5 py-2 rounded-sm text-sm">
                                    <UploadSimple size={14} className="inline mr-1" /> Upload
                                </Link>
                            </>
                        ) : me ? (
                            <>
                                <button
                                    data-testid="profile-follow-btn"
                                    onClick={onFollow}
                                    disabled={blockedByMe || blockedMe}
                                    className={`${following ? "btn-ghost" : "btn-primary"} px-5 py-2 rounded-sm text-sm font-heading uppercase tracking-wider disabled:opacity-40`}
                                >
                                    {following ? "Following" : "Follow"}
                                </button>
                                <Link
                                    data-testid="profile-message-btn"
                                    to={`/messages/${userId}`}
                                    className={`btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider flex items-center gap-1 ${blockedByMe || blockedMe ? "pointer-events-none opacity-40" : ""}`}
                                >
                                    <ChatCircle size={14} /> Message
                                </Link>
                                <button
                                    data-testid="profile-block-btn"
                                    onClick={onBlock}
                                    className={`btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider flex items-center gap-1 ${blockedByMe ? "text-wta-primary border-wta-primary" : ""}`}
                                >
                                    <Prohibit size={14} /> {blockedByMe ? "Unblock" : "Block"}
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/70 p-0 md:p-4" onClick={() => setEditing(false)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-wta-surface border border-wta-border md:rounded-sm flex flex-col h-screen md:h-auto md:max-h-[85vh]"
                        style={{ height: "100dvh", maxHeight: "100dvh" }}
                    >
                        <div className="font-heading text-2xl tracking-wider px-6 pt-6 pb-3 border-b border-wta-border shrink-0">Edit Profile</div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-6">
                        {/* Profile picture */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="relative w-20 h-20 rounded-sm bg-wta-surface2 border border-wta-border overflow-hidden flex items-center justify-center font-heading text-3xl shrink-0">
                                {profilePic ? <img src={profilePic} alt="" className="w-full h-full object-cover" /> : (name?.[0]?.toUpperCase() || "?")}
                                {uploadingPic && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs">…</div>}
                            </div>
                            <div className="flex-1">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={onFileSelected}
                                    className="hidden"
                                    data-testid="profile-pic-file"
                                />
                                <button
                                    type="button"
                                    onClick={onPickFile}
                                    disabled={uploadingPic}
                                    data-testid="upload-profile-pic"
                                    className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-1 disabled:opacity-50"
                                >
                                    <Camera size={14} weight="fill" /> {uploadingPic ? "Uploading…" : "Upload Photo"}
                                </button>
                                <div className="text-[10px] text-wta-muted mt-1">JPG, PNG, GIF · max 10MB</div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Name</label>
                                <input data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Bio</label>
                                <textarea data-testid="edit-bio" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Gender</label>
                                <select data-testid="edit-gender" value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm">
                                    <option value="">Prefer not to say</option>
                                    <option value="female">Female</option>
                                    <option value="male">Male</option>
                                    <option value="other">Other</option>
                                </select>
                                <div className="text-[10px] text-wta-muted mt-1">Required to join modeling competitions.</div>
                            </div>
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Or paste image URL</label>
                                <input data-testid="edit-pic" value={profilePic} onChange={(e) => setProfilePic(e.target.value)} placeholder="https://..." className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm" />
                            </div>

                            {/* Blocked users */}
                            <div className="pt-3 border-t border-wta-border">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs tracking-widest text-wta-muted uppercase">Blocked Users</label>
                                    <button type="button" data-testid="load-blocked-btn" onClick={loadBlockedList} className="text-xs text-wta-primary hover:underline">Refresh</button>
                                </div>
                                {blockedList.length === 0 ? (
                                    <div className="text-xs text-wta-muted mt-2">{"No one blocked. Tap 'Refresh' to load."}</div>
                                ) : (
                                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto pr-1">
                                        {blockedList.map((b) => (
                                            <div key={b.user.id} className="flex items-center justify-between gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2">
                                                <div className="text-sm truncate">@{b.user.name}</div>
                                                <button
                                                    data-testid={`unblock-${b.user.id}`}
                                                    onClick={() => onUnblockFromList(b.user.id)}
                                                    className="text-xs font-heading uppercase tracking-wider text-wta-primary hover:underline shrink-0"
                                                >
                                                    Unblock
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        </div>
                        {/* Sticky footer so Save/Cancel are always visible */}
                        <div className="shrink-0 border-t border-wta-border px-6 py-3 flex gap-3 bg-wta-surface" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
                            <button data-testid="edit-save" onClick={onSave} className="btn-primary px-5 py-2 rounded-sm text-sm flex-1">Save</button>
                            <button onClick={() => setEditing(false)} className="btn-ghost px-5 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-10">
                <div className="font-heading text-3xl tracking-wider uppercase mb-4">Posts</div>
                {blockedByMe ? (
                    <div data-testid="blocked-by-me-notice" className="bg-wta-surface border border-wta-border rounded-sm p-6 text-wta-muted flex items-center gap-3">
                        <Prohibit size={20} className="text-wta-primary" />
                        You blocked @{user.name}. Posts are hidden — unblock to see them again.
                    </div>
                ) : blockedMe ? (
                    <div data-testid="blocked-me-notice" className="bg-wta-surface border border-wta-border rounded-sm p-6 text-wta-muted">
                        Posts unavailable.
                    </div>
                ) : videos.length === 0 ? (
                    <div className="text-wta-muted">No posts yet.</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {videos.map((v) => (
                            <div key={v.id} className="aspect-[9/16] bg-wta-surface border border-wta-border rounded-sm overflow-hidden relative">
                                {v.thumbnail || (v.video_url && !v.video_url.match(/\.(mp4|webm|mov)/i)) ? (
                                    <img src={v.thumbnail || v.video_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <video src={v.video_url} muted className="w-full h-full object-cover" />
                                )}
                                <div className="absolute bottom-2 left-2 flex items-center gap-1 text-xs">
                                    <Heart size={12} weight="fill" /> {v.likes_count}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {cropSrc && (
                <ImageCropper
                    src={cropSrc}
                    onConfirm={onCropConfirm}
                    onCancel={() => setCropSrc(null)}
                />
            )}

            {/* Followers / Following list modal */}
            {followListKind && (
                <FollowListModal
                    userId={userId}
                    kind={followListKind}
                    onClose={() => setFollowListKind(null)}
                    onChange={load}
                />
            )}

            {/* Live Archive (own profile only) */}
            {isMe && (
                <>
                    <LiveAppealCard user={me} />
                    <div className="mt-10">
                        <LiveArchive />
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="bg-wta-surface border border-wta-border p-3 rounded-sm">
            <div className="text-[10px] tracking-widest uppercase text-wta-muted">{label}</div>
            <div className="font-heading text-2xl flex items-center gap-1">
                {Icon && <Icon size={16} className="text-wta-muted" />} {value}
            </div>
        </div>
    );
}

function StatButton({ label, value, icon: Icon, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="bg-wta-surface border border-wta-border p-3 rounded-sm text-left hover:border-wta-primary hover:bg-white/5 transition-colors"
        >
            <div className="text-[10px] tracking-widest uppercase text-wta-muted">{label}</div>
            <div className="font-heading text-2xl flex items-center gap-1">
                {Icon && <Icon size={16} className="text-wta-muted" />} {value}
            </div>
        </button>
    );
}
